import { type ApolloError, useQuery } from '@apollo/client';
import { Skeleton } from '@mui/material';
import type React from 'react';
import PDF_IMAGE from 'src/assets/pdfImage.png';
import { BoxImageStyles, ImageWrapperStyles } from 'src/components/CommonStyles/ImageDiv/ImageDivStyles';
import ImageBox from 'src/components/ImageBox/ImageBox';
import PDFThumbnail from 'src/components/PDF/PDFThumbnail';
import {
  FD_MIMETYPE_SLUG,
  FD_NAME_SLUG,
  MEDIA_ERROR,
  MEDIA_PATH,
  PDF,
  PROXY_FORMAT_SLUG,
  PROXY_OFFLINE,
  THUMBNAIL_FORMAT_SLUG,
  THUMBNAIL_OFFLINE,
} from 'src/constants';
import { buildFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { getMetadataValue } from 'src/helpers/common';
import { genMediaUrl } from 'src/helpers/generateCustomFields';
import { useMetaQ } from 'src/hooks/meta';
import MediaUnavailable from '../AkomiMediaCard/MediaUnavailable';

const FORMAT = 'FORMAT' as const;
const META = 'METADATA' as const;

type VariableType = typeof FORMAT | typeof META;

interface FormatQueryVariables {
  asset_Items_ObjectId?: string;
  asset_Id?: string;
  fmtType_Slug: string;
}

interface MetaQueryVariables {
  itemId?: string;
  globalId?: string;
  definitionSlugIn: string[];
}

interface FormatNode {
  id?: string;
  online?: boolean;
}

interface FormatsData {
  formats?: FormatNode[];
}

interface MetadataValue {
  definition?: {
    slug?: string;
  };
  content?: string;
}

interface MetadataData {
  metadata?: MetadataValue[];
}

interface AkomiCollectionMediaProps {
  itemId?: string;
  itemObjectId?: string;
  akomiMediaCardIndex?: number;
  onImageClick?: () => void;
}

export const AkomiCollectionMedia: React.FC<AkomiCollectionMediaProps> = ({ itemId, itemObjectId, onImageClick }) => {
  const getVariables = (type: VariableType): Partial<FormatQueryVariables | MetaQueryVariables> => {
    if (type === FORMAT) {
      return itemObjectId ? { asset_Items_ObjectId: itemObjectId } : { asset_Id: itemId };
    }
    if (type === META) {
      return itemObjectId ? { itemId: itemObjectId.toString() } : { globalId: itemId };
    }
    return {};
  };

  const {
    data: proxyFormatData,
    loading,
    error,
  } = useQuery<FormatsData>(buildFormatsQ(), {
    skip: !itemId && !itemObjectId,
    variables: { ...getVariables(FORMAT), fmtType_Slug: PROXY_FORMAT_SLUG } as FormatQueryVariables,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-only',
  });

  const {
    data: thumbnailData,
    loading: loadingThumb,
    error: errorThumb,
  } = useQuery<FormatsData>(buildFormatsQ(), {
    skip: !itemId && !itemObjectId,
    variables: { ...getVariables(FORMAT), fmtType_Slug: THUMBNAIL_FORMAT_SLUG } as FormatQueryVariables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const proxyFormat: FormatNode = proxyFormatData?.formats ? proxyFormatData.formats[0] : {};
  const thumbnailFormat: FormatNode = thumbnailData?.formats ? thumbnailData.formats[0] : {};

  const {
    data: formatData,
    loading: loadingFormatData,
    error: formatDataError,
  } = useMetaQ(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat?.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    false,
  ) as { data: MetadataData | undefined; loading: boolean; error: ApolloError | undefined };

  const {
    data: metadata,
    loading: loadingMetadata,
    error: metadataError,
  } = useMetaQ(
    {
      skip: !itemId && !itemObjectId,
      variables: { ...getVariables(META), definitionSlugIn: [FD_NAME_SLUG] },
    },
    false,
  ) as { data: MetadataData | undefined; loading: boolean; error: ApolloError | undefined };

  if ((!itemId && !itemObjectId) || loading || loadingThumb || loadingMetadata || loadingFormatData)
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255, 255, 255, 0.04)',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)',
              },
            }}
          />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );

  if (error || errorThumb || metadataError || formatDataError)
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={MEDIA_ERROR} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );

  const name = getMetadataValue(metadata as Record<string, unknown>, FD_NAME_SLUG);

  if (!proxyFormat?.online)
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={PROXY_OFFLINE} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );

  const mimetype = getMetadataValue(formatData as Record<string, unknown>, FD_MIMETYPE_SLUG) || '';

  if (!thumbnailFormat?.online && !mimetype?.includes(PDF)) {
    return (
      <ImageWrapperStyles>
        <BoxImageStyles>
          <MediaUnavailable text={THUMBNAIL_OFFLINE} />
        </BoxImageStyles>
      </ImageWrapperStyles>
    );
  }

  if (mimetype?.includes(PDF)) {
    return <PDFThumbnail onClickHandler={onImageClick} cardImageSrc={PDF_IMAGE} />;
  }

  const thumbnailFmt = genMediaUrl(MEDIA_PATH, thumbnailFormat?.id ?? '');
  return (
    <ImageBox className="customRightClick" imageBoxFormat={thumbnailFmt} onClickHandler={onImageClick} name={name} />
  );
};

export default AkomiCollectionMedia;
